import * as THREE from "three";
import { SplatPool } from "../paint-splats";
import type { PaintBall, PaintHit } from "../sim/backpack";
import type { SkaterEvent } from "../sim/state";

// Paintballs in flight and the splats they leave (#136), render only: the
// sim moves the balls (`Skater.pack.balls`) and raises a `splat` event per
// hit; this draws each ball and keeps the splats in a fixed pool
// (`SplatPool`), one instanced mesh each, so a shot never allocates. Plain
// unlit paint in every art style. The power washer's `wash` events (#151)
// wash splats off, one sim step's worth each.

/** How many balls can be drawn in flight at once (a shot every 0.3 s lives at most a second or so). */
const BALL_CAP = 24;
const BALL_RADIUS = 0.05;
/** A splat's size across (m), and how far it sits off the surface so it doesn't flicker into it. */
const SPLAT_SIZE = 0.55;
const SPLAT_LIFT = 0.012;

/** A paint blob: a wobbly disc with a few lobes, facing +z. */
function splatGeometry(): THREE.BufferGeometry {
  const shape = new THREE.Shape();
  const n = 28;
  for (let i = 0; i <= n; i++) {
    const a = (i / n) * Math.PI * 2;
    const r = 0.5 * (0.78 + 0.16 * Math.sin(5 * a + 0.6) + 0.08 * Math.sin(9 * a + 2.1) + 0.06 * Math.cos(3 * a));
    if (i === 0) shape.moveTo(Math.cos(a) * r, Math.sin(a) * r);
    else shape.lineTo(Math.cos(a) * r, Math.sin(a) * r);
  }
  return new THREE.ShapeGeometry(shape);
}

export class PaintFx {
  readonly root = new THREE.Group();
  readonly pool = new SplatPool();
  private balls: THREE.InstancedMesh;
  private splats: THREE.InstancedMesh;
  private m = new THREE.Matrix4();
  private q = new THREE.Quaternion();
  private spin = new THREE.Quaternion();
  private p = new THREE.Vector3();
  private n = new THREE.Vector3();
  private s = new THREE.Vector3();
  private c = new THREE.Color();

  /** `step` is the sim's fixed step: how long each `wash` event sprays for. */
  constructor(private step: number) {
    this.root.name = "paint";
    const ballMat = new THREE.MeshBasicMaterial({ color: 0xffffff });
    this.balls = new THREE.InstancedMesh(new THREE.SphereGeometry(BALL_RADIUS, 10, 8), ballMat, BALL_CAP);
    this.balls.count = 0;
    this.balls.frustumCulled = false;
    const splatMat = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      polygonOffset: true,
      polygonOffsetFactor: -2,
      polygonOffsetUnits: -2,
      side: THREE.DoubleSide,
    });
    this.splats = new THREE.InstancedMesh(splatGeometry(), splatMat, this.pool.cap);
    this.splats.frustumCulled = false;
    for (let i = 0; i < this.pool.cap; i++) {
      this.splats.setMatrixAt(i, this.m.makeScale(0, 0, 0));
      this.splats.setColorAt(i, this.c.set(0xffffff));
    }
    this.root.add(this.balls, this.splats);
  }

  /** Draws this frame's balls, adds a splat for each hit in `events` and washes paint off for each wash. */
  update(balls: readonly PaintBall[], events: readonly SkaterEvent[]): void {
    const count = Math.min(BALL_CAP, balls.length);
    for (let i = 0; i < count; i++) {
      const b = balls[i]!;
      this.balls.setMatrixAt(i, this.m.makeTranslation(b.x, b.y, b.z));
      this.balls.setColorAt(i, this.c.set(b.color));
    }
    this.balls.count = count;
    if (count > 0) {
      this.balls.instanceMatrix.needsUpdate = true;
      if (this.balls.instanceColor) this.balls.instanceColor.needsUpdate = true;
    }
    let added = false;
    for (const e of events) {
      if (e.type === "wash") {
        if (this.pool.wash(e.hit, this.step)) added = true;
        continue;
      }
      if (e.type !== "splat") continue;
      const i = this.pool.add(e.hit);
      this.splats.setColorAt(i, this.c.set(e.hit.color));
      added = true;
    }
    if (added) this.placeSplats();
  }

  /** Clears the park of paint (a new map). */
  clear(): void {
    this.pool.clear();
    this.placeSplats();
  }

  /** Sets every slot's matrix: facing out from its surface, turned by its slot, sized by the pool's fade. */
  private placeSplats(): void {
    for (let i = 0; i < this.pool.cap; i++) {
      const hit = this.pool.slots[i];
      if (!hit) {
        this.splats.setMatrixAt(i, this.m.makeScale(0, 0, 0));
        continue;
      }
      this.splats.setMatrixAt(i, this.splatMatrix(hit, i, this.pool.scale(i)));
    }
    this.splats.instanceMatrix.needsUpdate = true;
    if (this.splats.instanceColor) this.splats.instanceColor.needsUpdate = true;
  }

  private splatMatrix(hit: PaintHit, slot: number, scale: number): THREE.Matrix4 {
    this.n.set(hit.nx, hit.ny, hit.nz).normalize();
    this.q.setFromUnitVectors(Z, this.n);
    // Each slot turned its own way, so the lobes don't all line up.
    this.spin.setFromAxisAngle(Z, slot * 2.399);
    this.q.multiply(this.spin);
    this.p.set(hit.x, hit.y, hit.z).addScaledVector(this.n, SPLAT_LIFT);
    const size = SPLAT_SIZE * scale;
    return this.m.compose(this.p, this.q, this.s.set(size, size, size));
  }

  dispose(): void {
    for (const mesh of [this.balls, this.splats]) {
      mesh.geometry.dispose();
      (mesh.material as THREE.Material).dispose();
      mesh.dispose();
    }
  }
}

const Z = new THREE.Vector3(0, 0, 1);
